import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { APP_CONFIG } from '../../../configs/config';
import { mapApiDataToFieldGroups } from '../ScannerPage/utils';

export default function useContactDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialContact = location.state?.contact;

  // Maintain contact state so we can update it after editing
  const [contact, setContact] = useState(initialContact);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');

  const fieldGroups = useMemo(() => {
    if (!contact) {
      return { primaryFields: [], additionalFields: [] };
    }
    return mapApiDataToFieldGroups(contact);
  }, [contact]);

  const { primaryFields, additionalFields: initialAdditionalFields } =
    fieldGroups;

  const [primaryFieldValues, setPrimaryFieldValues] = useState(() => {
    const values = {};
    primaryFields.forEach((field) => {
      values[field.key] = field.value;
    });
    return values;
  });

  const [additionalFields, setAdditionalFields] = useState(
    initialAdditionalFields,
  );

  // Update field values when contact changes (after successful update)
  useEffect(() => {
    if (contact) {
      const fieldGroups = mapApiDataToFieldGroups(contact);
      const newPrimaryValues = {};
      fieldGroups.primaryFields.forEach((field) => {
        newPrimaryValues[field.key] = field.value;
      });
      setPrimaryFieldValues(newPrimaryValues);
      setAdditionalFields(fieldGroups.additionalFields);
    }
  }, [contact]);

  useEffect(() => {
    if (!contact) {
      navigate('/contacts');
    }
  }, [contact, navigate]);

  const handlePrimaryFieldChange = useCallback((fieldKey, value) => {
    setPrimaryFieldValues((current) => ({
      ...current,
      [fieldKey]: value,
    }));
  }, []);

  const handleAdditionalFieldLabelChange = useCallback((index, label) => {
    setAdditionalFields((items) => {
      const next = [...items];
      next[index] = {
        ...next[index],
        attribute: label,
      };
      return next;
    });
  }, []);

  const handleAdditionalFieldValueChange = useCallback((index, value) => {
    setAdditionalFields((items) => {
      const next = [...items];
      next[index] = {
        ...next[index],
        value,
      };
      return next;
    });
  }, []);

  const handleAddAdditionalField = useCallback(() => {
    setAdditionalFields((items) => [
      ...items,
      { attribute: '', value: '' },
    ]);
  }, []);

  const handleRemoveAdditionalField = useCallback((indexToRemove) => {
    setAdditionalFields((items) =>
      items.filter((_, index) => index !== indexToRemove),
    );
  }, []);

  const buildUpdatePayload = useCallback(() => {
    const payload = {
      username: contact.username,
      image_url: contact.image_url || null,
      title: primaryFieldValues.title || null,
      full_name: primaryFieldValues.full_name || '',
      position: primaryFieldValues.position || null,
      email: primaryFieldValues.email || null,
      phone_number: primaryFieldValues.phone_number || null,
      organization: primaryFieldValues.organization || null,
      address: primaryFieldValues.address || null,
      others: null,
    };

    if (Array.isArray(additionalFields) && additionalFields.length > 0) {
      const validEntries = additionalFields
        .filter((item) => item.attribute)
        .reduce((accumulator, item) => {
          accumulator[item.attribute] = item.value ?? '';
          return accumulator;
        }, {});
      if (Object.keys(validEntries).length > 0) {
        payload.others = validEntries;
      }
    }

    return payload;
  }, [contact, primaryFieldValues, additionalFields]);

  const updateContact = useCallback(async () => {
    const payload = buildUpdatePayload();

    const response = await fetch(
      `${APP_CONFIG.API_BASE_URL}/business-cards/update`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      let message = `Update failed: ${response.status}`;
      try {
        const data = await response.json();
        message = data?.detail || message;
      } catch (error) {
        /* ignore json parsing errors */
      }
      throw new Error(message);
    }

    return true;
  }, [buildUpdatePayload]);

  const handleConfirm = useCallback(async () => {
    if (!contact) return;

    setIsUpdating(true);
    setUpdateError('');

    try {
      await updateContact();
      navigate('/contacts');
      return true;
    } catch (error) {
      const message = error?.message || 'Unable to update contact.';
      setUpdateError(message);
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [contact, updateContact, navigate]);

  const handleConfirmInPlace = useCallback(async () => {
    if (!contact) return false;

    setIsUpdating(true);
    setUpdateError('');

    try {
      await updateContact();
      
      // Update the local contact state with the new values
      const updatedContact = {
        ...contact,
        title: primaryFieldValues.title || null,
        full_name: primaryFieldValues.full_name || '',
        position: primaryFieldValues.position || null,
        email: primaryFieldValues.email || null,
        phone_number: primaryFieldValues.phone_number || null,
        organization: primaryFieldValues.organization || null,
        address: primaryFieldValues.address || null,
        others: null,
      };

      // Add additional fields if they exist
      if (Array.isArray(additionalFields) && additionalFields.length > 0) {
        const validEntries = additionalFields
          .filter((item) => item.attribute)
          .reduce((accumulator, item) => {
            accumulator[item.attribute] = item.value ?? '';
            return accumulator;
          }, {});
        if (Object.keys(validEntries).length > 0) {
          updatedContact.others = validEntries;
        }
      }

      // Update contact state to reflect changes in view mode
      setContact(updatedContact);
      
      return true;
    } catch (error) {
      const message = error?.message || 'Unable to update contact.';
      setUpdateError(message);
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [contact, updateContact, primaryFieldValues, additionalFields]);

  const handleCancel = useCallback(() => {
    navigate('/contacts');
  }, [navigate]);

  const handleDelete = useCallback(async () => {
    if (!contact) return;

    setIsUpdating(true);
    setUpdateError('');

    try {
      const response = await fetch(
        `${APP_CONFIG.API_BASE_URL}/business-cards`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: contact.username,
            image_url: contact.image_url,
          }),
        },
      );

      if (!response.ok) {
        let message = `Delete failed: ${response.status}`;
        try {
          const data = await response.json();
          message = data?.detail || message;
        } catch (error) {
          /* ignore json parsing errors */
        }
        throw new Error(message);
      }

      navigate('/contacts');
    } catch (error) {
      const message = error?.message || 'Unable to delete contact.';
      setUpdateError(message);
    } finally {
      setIsUpdating(false);
    }
  }, [contact, navigate]);

  if (!contact) {
    return null;
  }

  return {
    contact,
    imageSrc: contact.image_url,
    primaryFields,
    primaryFieldValues,
    additionalFields,
    isUpdating,
    updateError,
    handlePrimaryFieldChange,
    handleAdditionalFieldLabelChange,
    handleAdditionalFieldValueChange,
    handleAddAdditionalField,
    handleRemoveAdditionalField,
    handleConfirm,
    handleConfirmInPlace,
    handleCancel,
    handleDelete,
  };
}


